import { jsonrepair } from 'jsonrepair';
import { getModel } from './model-provider';

/**
 * 增强的 JSON 解析和修复工具
 * 支持多层级修复策略，包括使用 LLM 进行结构化修复
 */

/**
 * 基础 JSON 修复函数
 */
export function safeParseJSON<T = any>(input: string): T | null {
  try {
    // 先直接尝试 parse
    return JSON.parse(input);
  } catch {
    try {
      // 用 jsonrepair 修复
      const fixed = jsonrepair(input);
      return JSON.parse(fixed);
    } catch (e) {
      console.error('JSON 修复失败:', e);
      return null;
    }
  }
}

/**
 * 使用 LLM 修复 JSON 的高级函数
 */
export async function repairJSONWithLLM<T = any>(
  brokenJson: string,
  expectedSchema?: string,
  maxRetries: number = 2,
  state?: any
): Promise<T | null> {
  const model = getModel(state || { model: 'openai' });
  
  const repairPrompt = `请修复以下损坏的 JSON 数据，确保输出有效的 JSON 格式：

损坏的 JSON:
\`\`\`
${brokenJson}
\`\`\`

${expectedSchema ? `期望的 Schema:
\`\`\`
${expectedSchema}
\`\`\`` : ''}

要求：
1. 修复语法错误（如缺失括号、逗号、引号等）
2. 补全不完整的结构
3. 保持原有数据的语义不变
4. 只输出修复后的 JSON，不要添加任何解释

修复后的 JSON:`;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔧 尝试使用 LLM 修复 JSON (第 ${attempt} 次)`);
      
      const response = await model.invoke([
        { role: 'user', content: repairPrompt }
      ]);
      
      const repairedJson = response.content.toString().trim();
      
      // 提取 JSON 内容（去除可能的代码块标记）
      const jsonMatch = repairedJson.match(/```(?:json)?\s*([\s\S]*?)\s*```/) || 
                        repairedJson.match(/^\s*(\{[\s\S]*\}|\[[\s\S]*\])\s*$/);
      
      const cleanJson = jsonMatch ? jsonMatch[1].trim() : repairedJson;
      
      // 尝试解析修复后的 JSON
      const parsed = JSON.parse(cleanJson);
      console.log(`✅ LLM 修复成功 (第 ${attempt} 次)`);
      return parsed as T;
      
    } catch (error) {
      console.warn(`❌ LLM 修复失败 (第 ${attempt} 次):`, error);
      
      if (attempt === maxRetries) {
        console.error('🚫 LLM JSON 修复达到最大重试次数');
        return null;
      }
    }
  }
  
  return null;
}

/**
 * 增强的 JSON 解析函数 - 包含多层修复策略
 */
export async function enhancedParseJSON<T = any>(
  input: string,
  expectedSchema?: string,
  enableLLMRepair: boolean = true,
  state?: any
): Promise<T | null> {
  // 第一层：直接解析
  try {
    return JSON.parse(input) as T;
  } catch (error) {
    console.log('📝 直接 JSON 解析失败，尝试基础修复');
  }
  
  // 第二层：使用 jsonrepair 修复
  const basicRepaired = safeParseJSON<T>(input);
  if (basicRepaired !== null) {
    console.log('✅ 基础 JSON 修复成功');
    return basicRepaired;
  }
  
  // 第三层：使用 LLM 修复（如果启用）
  if (enableLLMRepair) {
    console.log('🤖 尝试使用 LLM 进行智能 JSON 修复');
    const llmRepaired = await repairJSONWithLLM<T>(input, expectedSchema, 2, state);
    if (llmRepaired !== null) {
      console.log('✅ LLM 智能修复成功');
      return llmRepaired;
    }
  }
  
  console.error('🚫 所有 JSON 修复策略均失败');
  return null;
}

/**
 * 生成 Schema 描述字符串
 */
export function generateSchemaDescription(schema: any): string {
  if (typeof schema === 'object' && schema.shape) {
    // Zod schema
    const fields = Object.keys(schema.shape);
    return `{ ${fields.join(', ')} }`;
  }
  
  if (typeof schema === 'string') {
    return schema;
  }
  
  return JSON.stringify(schema, null, 2);
}

/**
 * 从文本中提取 JSON 内容
 */
export function extractJsonFromText(text: string): string {
  // 尝试匹配代码块中的 JSON
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }
  
  // 尝试匹配裸 JSON 对象或数组
  const jsonMatch = text.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (jsonMatch) {
    return jsonMatch[1].trim();
  }
  
  // 如果没有找到明显的 JSON，返回整个文本
  return text.trim();
}